import {useState, useEffect} from "react";
import {Typography, Paper, Box, CircularProgress, Alert} from "@mui/material";
import {useAuth} from "shared-lib";
import {
  getConnectedAccount,
  checkAccountStatus,
  createStripeAccount,
  refreshAccountLink,
  createInvoice,
  AccountData,
  AccountStatusData,
} from "../services/stripeService";
import {
  StepperComponent,
  CreateAccountStep,
  OnboardingStep,
  AccountSetupStep,
  CompletedStep,
} from "../components/StripeIntegration";
import { useVendorContext } from "../context/VendorContextProvider";

const IntegrateStripe = () => {
  const {currentUser} = useAuth();
  const {currentVendor} = useVendorContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [accountData, setAccountData] = useState<AccountData | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [invoiceCreating, setInvoiceCreating] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(false);

  const steps = ["Create Account", "Finish Stripe Onboarding", "Finish Account Setup"];

  useEffect(() => {
    // Check if vendor already has a Stripe account
    const checkExistingStripeAccount = async () => {
      if (!currentVendor || !currentVendor.id) return;

      try {
        setLoading(true);

        // Get connected account from service
        const account = await getConnectedAccount(currentVendor.id);

        if (account) {
          setAccountData(account);
          setDocumentId(account.documentId);

          // Determine which step to show
          if (account.accountStatus?.ready) {
            setActiveStep(3);
            setSuccess(true);
          } else if (account.accountStatus?.details_submitted) {
            setActiveStep(2);
          } else if (account.stripeAccountId) {
            setActiveStep(1);
          }

          // Check current account status with Stripe
          if (!success && account.stripeAccountId && account.documentId) {
            //TODO: might be a good idea not to update twice
            await updateAccountStatus(account.stripeAccountId, account.documentId);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
        setError("Failed to check Stripe account status");
      }
    };

    if (currentVendor) {
      checkExistingStripeAccount();
    }
  }, [currentVendor]);

  const updateAccountStatus = async (accountId: string, docId: string) => {
    try {
      const data = await checkAccountStatus(accountId, docId);

      // Update account status in state
      if (data.account) {
        const statusData: AccountStatusData = {
          ready: data.ready,
          details_submitted: data.account.details_submitted,
          charges_enabled: data.account.charges_enabled,
          payouts_enabled: data.account.payouts_enabled,
          setup_link: data.setup_link,
          link_expires_at: data.expires_at,
        };

        setAccountData((prev: any) => ({
          ...prev,
          accountStatus: statusData,
        }));

        // Update step based on account status
        if (data.ready) {
          setActiveStep(3);
          setSuccess(true);
        } else if (data.account.details_submitted) {
          setActiveStep(2);
        }
        //now all scenarios start at least at step 1
      }

      return data;
    } catch (err: any) {
      console.error("Error checking account status:", err);
      setError(err.message || "Failed to check account status");
      return null;
    }
  };

  const handleCreateStripeAccount = async () => {
    if (!currentUser || !currentUser.email || !currentVendor) {
      setError("User email and vendor are required to create a Stripe account");
      return;
    }

    try {
      setLoading(true);
      setError("");

      // Create Stripe account using service
      const result = await createStripeAccount({
        email: currentUser.email,
        userId: currentUser.uid,
        vendorId: currentVendor.id,
        country: "US",
      });

      setDocumentId(result.documentId);
      setAccountData({
        stripeAccountId: result.accountId,
        documentId: result.documentId,
        accountStatus: {
          ready: false,
          setup_link: result.accountLink?.url,
          link_expires_at: result.accountLink?.expires_at,
        },
      });

      setActiveStep(1);

      // Redirect to Stripe onboarding if URL is provided
      if (result.accountLink?.url) {
        window.location.href = result.accountLink.url;
      }

      setLoading(false);
    } catch (err: any) {
      console.error(err);
      setLoading(false);
      setError(err.message || "Failed to create Stripe account");
    }
  };

  const handleCheckAccountStatus = async () => {
    if (!accountData || !accountData.stripeAccountId || !documentId) {
      setError("No Stripe account to check");
      return;
    }

    try {
      setLoading(true);
      setError("");

      await updateAccountStatus(accountData.stripeAccountId, documentId);

      if (accountData.accountStatus) {
        if (accountData.accountStatus.ready) {
          setSuccess(true);
          setActiveStep(3);
        } else if (accountData.accountStatus.details_submitted) {
          setActiveStep(2);

          window.location.href = "https://dashboard.stripe.com/dashboard";
        } else if (accountData.accountStatus.setup_link) {
          // Check if the link is still valid
          const now = new Date();
          const expiresAt = new Date(accountData.accountStatus.link_expires_at! * 1000); // Convert to milliseconds

          if (expiresAt > now) {
            window.location.href = accountData.accountStatus.setup_link;
          } else {
            // Refresh the link if expired
            const refreshedLink = await refreshAccountLink(accountData.stripeAccountId);

            if (refreshedLink.url) {
              window.location.href = refreshedLink.url;
            }
          }
        }
      }

      setLoading(false);
    } catch (err: any) {
      console.error(err);
      setLoading(false);
      setError(err.message || "Failed to check account status");
    }
  };

  const handleCreateInvoice = async (email: string, amount: string, description: string, bidId?: string) => {
    if (!accountData || !accountData.stripeAccountId) {
      setError("No Stripe account to create invoice with");
      return;
    }

    if (!email) {
      setError("Customer email is required");
      return;
    }

    try {
      setInvoiceCreating(true);
      setError("");

      // Create invoice using service
      const result = await createInvoice({
        email: email,
        items: [
          {
            amount: parseInt(amount) * 100, // Convert to cents
            currency: "usd",
            quantity: 1,
            description: description,
          },
        ],
        connectedAccountId: accountData.stripeAccountId,
        daysUntilDue: 14,
        bidId: bidId,
      });

      if (result.success) {
        setInvoiceCreated(true);
      } else if (result.error) {
        throw new Error(result.error);
      }

      setInvoiceCreating(false);
    } catch (err: any) {
      console.error(err);
      setInvoiceCreating(false);
      setError(err.message || "Failed to create invoice");
    }
  };

  // Render content based on loading state
  const renderContent = () => {
    if (loading && !accountData) {
      return <CircularProgress />;
    }

    switch (activeStep) {
      case 0:
        return <CreateAccountStep loading={loading} onCreateAccount={handleCreateStripeAccount} />;
      case 1:
        return <OnboardingStep loading={loading} onContinueOnboarding={handleCheckAccountStatus} />;
      case 2:
        return <AccountSetupStep loading={loading} onCheckStatus={handleCheckAccountStatus} />;
      default:
        return null;
    }
  };

  
  if(loading) {
    return (
      <Paper elevation={0} sx={{p: 3, mb: 3}}>
        <CircularProgress />
      </Paper>
    );
  }
  

  if (activeStep === 3) {
    return (
      <Paper elevation={0} sx={{p: 3, mb: 3}}>     
          <CompletedStep
          accountData={accountData}
          invoiceCreating={invoiceCreating}
          invoiceCreated={invoiceCreated}
          onCreateInvoice={handleCreateInvoice}
        />
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{p: 3, mb: 3}}>
      <Typography variant="h4" component="h1" gutterBottom>
        Integrate with Stripe
      </Typography>

      <Typography variant="body1" paragraph>
        Connect your Stripe account to receive payments through our platform. We use Stripe Connect to securely process
        payments without handling your banking details.
      </Typography>

      {error && (
        <Alert severity="error" sx={{my: 2}}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{my: 2}}>
          Your Stripe account has been successfully integrated!
        </Alert>
      )}

      <StepperComponent activeStep={activeStep} steps={steps} />

      <Box sx={{mt: 4, display: "flex", flexDirection: "column", alignItems: "center"}}>{renderContent()}</Box>
    </Paper>
  );
};

export default IntegrateStripe;
